import mediaQuery from "@utils/mediaQuery";
import styled from "styled-components";
import { useState } from "react";

interface Props {
  title: string;
  content: string;
  buttonText?: string;
  buttonOnClick?: () => void;
}

const Container = styled.div`
  display: flex;
  width: calc(100% - 80px);
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  z-index: 5;

  border-radius: 12px;
  background: var(--bg-card);

  position: absolute;
  top: 50%;

  ${mediaQuery("tablet")`
  top: auto;
  bottom: 40px;
  right: 40px;
  width: 513px;
  `}
`;

const Title = styled.div`
  font-size: var(--font-size-xl);
  font-weight: 700;
  line-height: 25.6px;
`;

const Content = styled.div`
  color: #233863;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-base);
  margin-bottom: 16px;

  white-space: pre-line;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
`;

const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--interactive-primary);

  &:hover {
    accent-color: var(--accent-divider);
  }
`;

const CheckboxLabel = styled.label`
  color: #233863;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-base);
  cursor: pointer;
  user-select: none;
`;

const Button = styled.button<{ disabled?: boolean }>`
  border: none;
  margin: 0;
  padding: 0;
  appearance: none;

  display: flex;
  height: 40px;
  padding: 10px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  background: ${props => props.disabled ? 'var(--color-neutral-300)' : 'var(--interactive-primary)'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  color: ${props => props.disabled ? '#666' : 'var(--bg-card)'};
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: var(--letter-spacing-tight);

  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.disabled ? 'var(--color-neutral-300)' : 'var(--accent-divider)'};
  }
`;

const Disclaimer = ({ title, content, buttonText, buttonOnClick }: Props) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleAccept = () => {
    if (isChecked && buttonOnClick) {
      buttonOnClick();
    }
  };

  return (
    <Container>
      <Title>{title}</Title>
      <Content>{content}</Content>
      
      <CheckboxContainer>
        <CheckboxInput
          type="checkbox"
          id="disclaimer-checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
        <CheckboxLabel htmlFor="disclaimer-checkbox">
          I have read and understood the disclaimer
        </CheckboxLabel>
      </CheckboxContainer>

      {buttonText && (
        <Button 
          onClick={handleAccept} 
          disabled={!isChecked}
        >
          {buttonText}
        </Button>
      )}
    </Container>
  );
};

export default Disclaimer;